import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Input, Label, Spinner } from 'reactstrap';
import { api } from '../api/client.js';
import { WeekSelector } from '../components/WeekSelector.jsx';
import { GameCard } from '../components/GameCard.jsx';
import { useCurrentWeek } from '../hooks/useCurrentWeek.js';
import { formatKickoff } from '../utils/dates.js';

export function Picks() {
  const current = useCurrentWeek();
  const [season, setSeason] = useState(null);
  const [week, setWeek] = useState(null);

  const [schedule, setSchedule] = useState(null);
  const [selections, setSelections] = useState({});
  const [tiebreaker, setTiebreaker] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (current.season && season === null) {
      setSeason(current.season);
      setWeek(current.week);
    }
  }, [current, season]);

  const load = useCallback(async () => {
    if (!season || !week) return;

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const [weekSchedule, myPicks] = await Promise.all([
        api.week(season, week),
        api.myPicks(season, week),
      ]);

      setSchedule(weekSchedule);
      setSelections(Object.fromEntries(myPicks.picks.map((p) => [p.gameId, p.selectedTeamId])));
      setTiebreaker(myPicks.tiebreakerTotalPoints ?? '');
    } catch (err) {
      setError(err.message);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  }, [season, week]);

  useEffect(() => {
    load();
  }, [load]);

  const tiebreakerGame = useMemo(
    () => schedule?.games.find((g) => g.isTiebreakerGame) ?? null,
    [schedule]
  );

  const openGames = useMemo(
    () => schedule?.games.filter((g) => !g.isLocked) ?? [],
    [schedule]
  );

  function handleSelect(gameId, teamId) {
    setSelections((prev) => ({ ...prev, [gameId]: teamId }));
    setStatus(null);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setStatus(null);

    try {
      const payload = {
        seasonYear: season,
        weekNumber: week,
        picks: Object.entries(selections).map(([gameId, selectedTeamId]) => ({
          gameId: Number(gameId),
          selectedTeamId,
        })),
        tiebreakerTotalPoints: tiebreaker === '' ? null : Number(tiebreaker),
      };

      const result = await api.submitPicks(payload);
      setStatus(result);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (current.loading) {
    return <div className="text-center py-5"><Spinner /></div>;
  }

  if (current.error) {
    return <Alert fade={false} color="warning">No schedule has been synced yet. An admin needs to run a sync first.</Alert>;
  }

  const window = schedule?.window;
  const tiebreakerLocked = window?.allLocked ?? false;

  return (
    <div>
      <h1 className="h3 mb-3">My picks</h1>

      <WeekSelector season={season} week={week} disabled={loading}
        onChange={(s, w) => { setSeason(s); setWeek(w); }} />

      {error && <Alert fade={false} color="danger">{error}</Alert>}

      {status && (
        <Alert fade={false} color={status.rejected.length > 0 ? 'warning' : 'success'}>
          Saved {status.accepted} pick{status.accepted === 1 ? '' : 's'}.
          {status.rejected.length > 0 && (
            <ul className="mb-0 mt-2">
              {status.rejected.map((reason) => <li key={reason}>{reason}</li>)}
            </ul>
          )}
        </Alert>
      )}

      {window && (
        <Alert fade={false} color={window.isOpen ? 'info' : 'secondary'} className="small">
          {!window.hasOpened && <>Picks open {formatKickoff(window.opensAtUtc)}.</>}
          {window.isOpen && (
            <>
              Open now. The opening game locks at {formatKickoff(window.firstGameLocksAtUtc)};
              every other game and the tiebreaker lock at {formatKickoff(window.remainingGamesLockAtUtc)}.
            </>
          )}
          {window.allLocked && <>All picks for this week are locked.</>}
        </Alert>
      )}

      {loading && <div className="text-center py-4"><Spinner /></div>}

      {!loading && schedule && (
        <>
          <div className="game-grid">
            {schedule.games.map((game) => (
              <GameCard key={game.id} game={game} selectedTeamId={selections[game.id]}
                locked={game.isLocked} onSelect={handleSelect} />
            ))}
          </div>

          <div className="mt-4 p-3 border rounded bg-body-tertiary">
            <Label for="tiebreaker" className="fw-semibold">
              Tiebreaker &mdash; combined score of {tiebreakerGame
                ? `${tiebreakerGame.away.abbreviation} @ ${tiebreakerGame.home.abbreviation}`
                : 'the last game of the week'}
            </Label>
            <Input id="tiebreaker" type="number" min="0" style={{ maxWidth: 160 }}
              value={tiebreaker} disabled={tiebreakerLocked}
              onChange={(e) => setTiebreaker(e.target.value)} />
            <small className="text-muted d-block mt-1">
              Used only when players tie on correct picks. Closest guess wins the week.
            </small>
          </div>

          <div className="mt-3 d-flex align-items-center gap-3">
            <Button color="primary" onClick={handleSave} disabled={saving || openGames.length === 0}>
              {saving ? <Spinner size="sm" /> : 'Save picks'}
            </Button>
            {openGames.length === 0 && (
              <span className="text-muted small">Nothing left to change this week.</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
